/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Grid,
  Checkbox,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  TextField,
} from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyGetPaymentDetailsQuery,
  useLazyGetPaymentMethodByIdQuery,
  usePostPaymentDetailsMutation,
  usePostPaymentMethodMutation,
  usePutPaymentDetailsMutation,
} from "@api/applications";
import { RootState } from "@store/store";
import { useLazyGetPaymentMethodQuery } from "@api/catalogs";
import CustomRadioGroup from "@components/CustomRadioGroup";
import { InactivityHandler } from "@components/InactivityHandler";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, DynamicQuestionAnswerInput } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import useSolicitudCreditoForm from "@hooks/useSolicitudCreditoForm";
import CustomDynamicQuestionField from "@components/CustomDynamicQuestionField";
import { useLazyGetDynamicQuestionByApplicationQuery, usePostDynamicQuestionMutation } from "@api/dynamicQuestion";
import {
  buildDynamicQuestionAnswerItems,
  buildDynamicAnswersValidationSchema,
  isDynamicQuestionVisible,
  mapDynamicQuestionAnswerRecordsToFormValues,
} from "@helpers/dynamicQuestionsHelpers";

const InformacionTransaccional = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const applicationData = useSelector((state: RootState) => state.register.application);

  const [triggerPaymentMethod] = usePostPaymentMethodMutation();
  const [triggerPaymentDetails] = usePostPaymentDetailsMutation();
  const [triggerPutPaymentDetails] = usePutPaymentDetailsMutation();
  const [getPaymentMethod] = useLazyGetPaymentMethodQuery();
  const [getDetalles] = useLazyGetPaymentDetailsQuery();
  const [getMetodoByApplication] = useLazyGetPaymentMethodByIdQuery();
  const [postDynamicQuestion] = usePostDynamicQuestionMutation();
  const [getDynamicQuestion] = useLazyGetDynamicQuestionByApplicationQuery();

  const [idOtherPayment, setIdOtherPayment] = useState("");
  const [idDetalle, setIdDetalle] = useState<string | null>(null);
  const [paymentMethodList, setPaymentMethodList] = useState<Array<{ id: string; description: string }>>([]);

  const { questions, isVisible, isRequired, requiredField } = useSolicitudCreditoForm("step_2");

  const formik = useFormik({
    initialValues: {
      paymentMethods: [] as string[],
      otherPaymentMethod: "",
      advancePayments: "",
      resourcesTypes: "",
      otro: false,
      // Respuestas de las preguntas dinámicas (step_2.questions), indexadas por "code".
      // string para boolean/single_choice/open, string[] para multiple_choice.
      dynamicAnswers: {} as Record<string, DynamicQuestionAnswerInput>,
    },
    validationSchema: yup.object().shape({
      paymentMethods: isRequired("paymentMethods", true)
        ? yup.array().min(1).of(yup.string().required()).required()
        : yup.array().notRequired(),
      otro: yup.boolean(),
      otherPaymentMethod: yup.string().when("otro", {
        is: true,
        then: (schema) =>
          isRequired("otherPaymentMethod", true)
            ? schema.required("Este campo es requerido cuando la opción otro esta seleccionada")
            : schema.notRequired(),
        otherwise: (schema) => schema.notRequired(),
      }),
      advancePayments: requiredField(yup.string(), "advancePayments", true, "Campo requerido"),
      resourcesTypes: requiredField(yup.string(), "resourcesTypes", true, "Campo requerido"),
      dynamicAnswers: buildDynamicAnswersValidationSchema(questions),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        await triggerPaymentMethod({
          solicitudId: applicationData.id,
          body: {
            payment_methods: values.paymentMethods.map((id) => ({
              payment_method_id: id,
              other_payment_method: id === idOtherPayment ? values.otherPaymentMethod : null,
            })),
          },
        }).unwrap();

        // Si no hay configuración (o el catálogo no cargó) "questions" viene vacío y no se
        // ejecuta el POST, para no mandar un body vacío/inválido.
        if (questions.length > 0) {
          // No se mandan respuestas de preguntas que quedaron ocultas por "visible_when".
          const visibleQuestions = questions.filter((question) =>
            isDynamicQuestionVisible(question, values.dynamicAnswers),
          );
          const dynamicQuestionItems = buildDynamicQuestionAnswerItems(visibleQuestions, values.dynamicAnswers);

          if (dynamicQuestionItems.length > 0) {
            await postDynamicQuestion({
              solicitudId: applicationData.id,
              body: dynamicQuestionItems,
            }).unwrap();
          }
        }

        const body = {
          advance_payments: values.advancePayments === "Si",
          own_account: false,
          own_resources: values.resourcesTypes === "Propios",
        };

        if (idDetalle) {
          await triggerPutPaymentDetails({
            solicitudId: applicationData.id,
            detailId: idDetalle,
            body,
          }).unwrap();
        } else {
          await triggerPaymentDetails({
            solicitudId: applicationData.id,
            body,
          }).unwrap();
        }

        navigate("/producto/creditosimple/solicitud_credito_3");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart2-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  // const savePaymentMethods = async (
  //   e:
  //     | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  //     | React.FocusEvent<HTMLInputElement, Element>
  //     | React.SyntheticEvent<Element, Event>,
  // ) => {
  //   try {
  //     formik.handleBlur(e);

  //     const { values, errors } = formik;

  //     const { paymentMethods, otherPaymentMethod, advancePayments, resourcesTypes } = errors;
  //     if (paymentMethods || otherPaymentMethod || advancePayments || resourcesTypes) {
  //       return;
  //     }

  //     const payload = {
  //       payment_methods: values.paymentMethods.map((id) => ({
  //         payment_method_id: id,
  //         other_payment_method: id === idOtherPayment ? values.otherPaymentMethod : null,
  //       })),
  //     };

  //     await triggerPaymentMethod({
  //       solicitudId: applicationData.id,
  //       body: payload,
  //     }).unwrap();
  //   } catch (error) {
  //     console.error("Error guardando información de pago:", error);
  //   }
  // };

  // const saveDetails = async (
  //   e:
  //     | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  //     | React.FocusEvent<HTMLInputElement, Element>
  //     | React.SyntheticEvent<Element, Event>,
  // ) => {
  //   try {
  //     formik.handleBlur(e);

  //     const { values, errors } = formik;

  //     const { advancePayments, resourcesTypes } = errors;
  //     if (advancePayments || resourcesTypes) {
  //       return;
  //     }

  //     const payload = {
  //       advance_payments: values.advancePayments === "Si",
  //       own_account: false,
  //       own_resources: values.resourcesTypes === "Propios",
  //     };

  //     if (idDetalle) {
  //       await triggerPutPaymentDetails({
  //         solicitudId: applicationData.id,
  //         detailId: idDetalle,
  //         body: payload,
  //       }).unwrap();
  //     } else {
  //       const newDetail = await triggerPaymentDetails({
  //         solicitudId: applicationData.id,
  //         body: payload,
  //       }).unwrap();
  //       setIdDetalle(newDetail.data.id);
  //     }
  //   } catch (error) {
  //     console.error("Error guardando información de pago:", error);
  //   }
  // };

  const getDetallesWrapped = async () => {
    try {
      return await getDetalles({ solicitudId: applicationData.id }).unwrap();
    } catch (error) {
      return null;
    }
  };

  const fetchInitialData = async () => {
    try {
      const [detallesResponse, metodoResponse, paymentCatalog] = await Promise.all([
        getDetallesWrapped(),
        getMetodoByApplication({ solicitudId: applicationData.id }).unwrap(),
        getPaymentMethod().unwrap(),
      ]);

      const detallesData = detallesResponse?.data;
      const metodoData = metodoResponse.data;

      // buscar el id del metodo de pago "OTRO"
      const otherPaymentMethod = paymentCatalog.find((item) => item.code === "CPIOT");
      const paymentMethodIds = metodoData?.map((item) => item.payment_method?.id) ?? [];
      const paymentMethodInput = metodoData?.find((item) => item.payment_method?.id === otherPaymentMethod?.id);

      formik.setFieldValue("resourcesTypes", detallesData ? (detallesData.own_resources ? "Propios" : "Tercero") : "");
      formik.setFieldValue("advancePayments", detallesData ? (detallesData.advance_payments ? "Si" : "No") : "");
      formik.setFieldValue("paymentMethods", paymentMethodIds);
      formik.setFieldValue("otherPaymentMethod", paymentMethodInput?.other_payment_method || "");

      setIdDetalle(detallesData?.id ?? null);
      setIdOtherPayment(otherPaymentMethod?.id ?? "");
      setPaymentMethodList(paymentCatalog);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "SolicitudCreditoPart2-fetchInitialData",
        },
      });
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  
  useEffect(() => {
    if (questions.length === 0) return;

    (async () => {
      try {
        const dynamicQuestionAnswers = await getDynamicQuestion(applicationData.id).unwrap();
        formik.setFieldValue(
          "dynamicAnswers",
          mapDynamicQuestionAnswerRecordsToFormValues(dynamicQuestionAnswers, questions),
        );
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart2-fetchDynamicQuestionAnswers",
          },
        });
      }
    })();
  }, [questions.length]);

  useEffect(() => {
    const isOtherSelected = formik.values.paymentMethods.includes(idOtherPayment);
    formik.setFieldValue("otro", isOtherSelected);
  }, [formik.values.paymentMethods, idOtherPayment]);

  // Si una pregunta con "visible_when" deja de estar visible (ej. "pagos_anticipados" pasa de
  // "si" a "no"), se limpia su respuesta: si no, se guardaría una respuesta de una pregunta que
  // ya no se le mostró al cliente con esa condición.
  useEffect(() => {
    questions.forEach((question) => {
      if (!question.visible_when) return;

      const currentValue = formik.values.dynamicAnswers[question.code];
      const hasValue = Array.isArray(currentValue) ? currentValue.length > 0 : Boolean(currentValue);
      if (!hasValue) return;

      if (!isDynamicQuestionVisible(question, formik.values.dynamicAnswers)) {
        formik.setFieldValue(`dynamicAnswers.${question.code}`, question.type === "multiple_choice" ? [] : "");
      }
    });
  }, [formik.values.dynamicAnswers, questions]);

  const handleOnClickRestart = () => {
    navigate("/producto/creditosimple/avance_expediente_digital");
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Solicitud de crédito
          </Typography>

          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            mt={2}
          >
            <strong>2</strong> de 3
          </Typography>

          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            fontWeight={600}
            mt={3}
          >
            Información transaccional
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            {isVisible("paymentMethods") && (
              <Box textAlign={"left"} my={2}>
                <Typography variant="body1" color={theme?.palette?.primary?.dark}>
                  ¿De qué forma realizarás tus pagos?
                </Typography>
                <FormGroup>
                  {paymentMethodList.length > 0 ? (
                    paymentMethodList.map((paymentMethod, index) => (
                      <FormControlLabel
                        key={index}
                        color={theme?.palette?.primary?.dark}
                        control={
                          <Checkbox
                            key={paymentMethod.id}
                            name="paymentMethods"
                            checked={formik.values.paymentMethods.includes(
                              paymentMethod.id as `${string}-${string}-${string}-${string}-${string}`,
                            )}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={paymentMethod.id}
                          />
                        }
                        label={paymentMethod.description}
                      />
                    ))
                  ) : (
                    <>
                      <Skeleton variant="text" width={300} height={40}></Skeleton>
                      <Skeleton variant="text" width={300} height={40}></Skeleton>
                      <Skeleton variant="text" width={300} height={40}></Skeleton>
                      <Skeleton variant="text" width={300} height={40}></Skeleton>
                    </>
                  )}
                </FormGroup>
                {formik.values.otro && isVisible("otherPaymentMethod") && (
                  <TextField
                    fullWidth
                    id="otherPaymentMethod"
                    name="otherPaymentMethod"
                    label="Especifica el método de pago"
                    value={formik.values.otherPaymentMethod}
                    required={isRequired("otherPaymentMethod", true)}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.otherPaymentMethod && Boolean(formik.errors.otherPaymentMethod)}
                    helperText={formik.touched.otherPaymentMethod && formik.errors.otherPaymentMethod}
                    margin="normal"
                  />
                )}
              </Box>
            )}
            {isVisible("advancePayments") && (
              <Box textAlign={"left"} my={2}>
                <CustomRadioGroup
                  label={"¿Realizarás pagos anticipados?"}
                  name={"advancePayments"}
                  value={formik.values.advancePayments}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.advancePayments && Boolean(formik.errors.advancePayments)}
                  helperText={formik.touched.advancePayments && formik.errors.advancePayments}
                  options={[
                    {
                      label: "Si",
                      value: "Si",
                    },
                    {
                      label: "No",
                      value: "No",
                    },
                  ]}
                />
              </Box>
            )}
            {isVisible("resourcesTypes") && (
              <Box textAlign={"left"} my={2}>
                <CustomRadioGroup
                  label={"¿Los recursos son propios o de un tercero?"}
                  name={"resourcesTypes"}
                  value={formik.values.resourcesTypes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.resourcesTypes && Boolean(formik.errors.resourcesTypes)}
                  helperText={formik.touched.resourcesTypes && formik.errors.resourcesTypes}
                  options={[
                    {
                      label: "Propios",
                      value: "Propios",
                    },
                    {
                      label: "De un tercero",
                      value: "Tercero",
                    },
                  ]}
                />
              </Box>
            )}

            {questions
              .filter((question) => isDynamicQuestionVisible(question, formik.values.dynamicAnswers))
              .map((question) => {
                const touchedAnswer = formik.touched.dynamicAnswers?.[question.code];
                const answerError = (formik.errors.dynamicAnswers as Record<string, string> | undefined)?.[
                  question.code
                ];
                return (
                  <Box textAlign={"left"} my={2} key={question.code}>
                    <CustomDynamicQuestionField
                      question={question}
                      value={
                        formik.values.dynamicAnswers[question.code] ?? (question.type === "multiple_choice" ? [] : "")
                      }
                      onChange={(newValue) => formik.setFieldValue(`dynamicAnswers.${question.code}`, newValue)}
                      onBlur={formik.handleBlur}
                      disabled={formik.isSubmitting}
                      error={Boolean(touchedAnswer && answerError)}
                      helperText={touchedAnswer && answerError}
                    />
                  </Box>
                );
              })}

            <Grid container columnSpacing={1} my={4}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="blue_outlined"
                  sx={{ padding: "0", height: "100%" }}
                  onClick={handleOnClickRestart}
                  disabled={formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="default"
                  disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionTransaccional;
